/**
 * Client-side image preparation: crop to a target aspect with pan/zoom, keep a
 * safe-zone overlay so nothing important is cut, then resize (never upscale)
 * and compress before upload, so a phone on festival wifi sends a few hundred
 * KB instead of a camera original. The server re-checks and normalizes every
 * upload anyway (image.server.ts): nothing here may assume a browser can
 * encode, or even open, a given format.
 */

export const MAX_UPLOAD_DIMENSION = 1600
/** Most a prepared upload may weigh; steps down in size/quality until it fits */
export const UPLOAD_BUDGET_BYTES = 1.5 * 1024 * 1024
/** Bigger originals are refused before uploading anything (server limit) */
export const MAX_ORIGINAL_BYTES = 30 * 1024 * 1024

/** Tried in order until the file fits the budget: [share of the size, quality] */
const ENCODE_STEPS = [
	[1, 0.85],
	[1, 0.75],
	[0.75, 0.75],
	[0.5, 0.7],
] as const

/** Safari refuses (silently) canvases above 4096×4096 */
const MAX_CANVAS_EDGE = 4096

const UNREADABLE =
	'Klarte ikkje å opne biletet. Prøv eit anna bilete (JPEG, PNG, WebP eller HEIC).'

export type PrepareOptions = {
	/** Target aspect (w/h). Null = keep original aspect (no crop, only resize). */
	aspect: number | null
	/** Floor on the largest source edge (px) after transform */
	maxDimension?: number
	/** Relative area of the frame that must contain the subject (0..1) */
	safeZone?: number
}

export type PreparedImage = {
	file: File
	width: number
	height: number
}

export type LoadedImage = {
	img: HTMLImageElement
	/** What `img` shows: the picked file, or the server's JPEG of it */
	blob: Blob
	revoke: () => void
}

function makeCanvas(width: number, height: number) {
	const canvas = document.createElement('canvas')
	canvas.width = Math.max(1, Math.round(width))
	canvas.height = Math.max(1, Math.round(height))
	return canvas
}

/** Hand a canvas' pixel memory back now (iOS caps the total, GC is slow) */
function release(canvas: HTMLCanvasElement) {
	canvas.width = 0
	canvas.height = 0
}

function context(canvas: HTMLCanvasElement) {
	const ctx = canvas.getContext('2d')
	if (!ctx) throw new Error('Canvas ikkje støtta')
	ctx.imageSmoothingEnabled = true
	ctx.imageSmoothingQuality = 'high'
	return ctx
}

function sizeOf(source: HTMLCanvasElement | HTMLImageElement) {
	return source instanceof HTMLImageElement
		? { width: source.naturalWidth, height: source.naturalHeight }
		: { width: source.width, height: source.height }
}

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		img.onload = () =>
			img.naturalWidth && img.naturalHeight
				? resolve(img)
				: reject(new Error(UNREADABLE))
		img.onerror = () => reject(new Error(UNREADABLE))
		img.src = src
	})
}

async function decode(blob: Blob): Promise<LoadedImage> {
	const url = URL.createObjectURL(blob)
	try {
		const img = await loadImage(url)
		return { img, blob, revoke: () => URL.revokeObjectURL(url) }
	} catch (error) {
		URL.revokeObjectURL(url)
		throw error
	}
}

/**
 * Open any picture: what the browser can show itself, else (HEIC outside
 * Safari, TIFF, …) the server's JPEG of it.
 */
export async function loadFileImage(file: File): Promise<LoadedImage> {
	if (/^(video|audio|text)\//.test(file.type)) {
		throw new Error('Det ser ikkje ut som eit bilete. Vel eit bilete.')
	}
	if (file.size > MAX_ORIGINAL_BYTES) {
		throw new Error(
			`Biletet er for stort (maks ${MAX_ORIGINAL_BYTES / 1024 / 1024} MB).`,
		)
	}
	try {
		return await decode(file)
	} catch {
		return await decode(await convertOnServer(file))
	}
}

async function convertOnServer(file: File): Promise<Blob> {
	const body = new FormData()
	body.append('file', file)
	let response: Response
	try {
		response = await fetch('/resources/image-convert', { method: 'POST', body })
	} catch {
		throw new Error(
			'Fekk ikkje kontakt med serveren for å opne biletet. Sjekk nettet og prøv igjen.',
		)
	}
	if (
		response.ok &&
		response.headers.get('Content-Type')?.startsWith('image/')
	) {
		return response.blob()
	}
	// 400/413/415 carry a message for the person uploading
	const message =
		response.status >= 400 && response.status < 500
			? await response.text().catch(() => '')
			: ''
	throw new Error(message.trim() || UNREADABLE)
}

/**
 * Draw a cover-crop of `img` into a canvas of `boxWidth x boxWidth / aspect`,
 * with zoom relative to the fit (1 = image just covers the box) and the
 * current pan offset (clamped to the drawable area downstream).
 */
export function drawCrop(
	img: HTMLImageElement,
	opts: {
		boxWidth: number
		aspect: number
		/** zoom relative to cover-fit (1 = image barely covers the box) */
		zoom: number
		/**
		 * Pan offset as a fraction (-1..1) of the allowed travel in each axis
		 * (0/0 = centered).
		 */
		offsetX: number
		offsetY: number
	},
) {
	const boxWidth = opts.boxWidth
	const boxHeight = opts.boxWidth / opts.aspect
	const canvas = makeCanvas(boxWidth, boxHeight)
	const ctx = context(canvas)

	const baseScale = Math.max(
		boxWidth / img.naturalWidth,
		boxHeight / img.naturalHeight,
	)
	const scale = baseScale * opts.zoom
	const scaledW = img.naturalWidth * scale
	const scaledH = img.naturalHeight * scale

	const maxOffsetX = Math.max(0, (scaledW - boxWidth) / 2)
	const maxOffsetY = Math.max(0, (scaledH - boxHeight) / 2)
	const dx = boxWidth / 2 + opts.offsetX * maxOffsetX - scaledW / 2
	const dy = boxHeight / 2 + opts.offsetY * maxOffsetY - scaledH / 2

	ctx.drawImage(img, dx, dy, scaledW, scaledH)
	return canvas
}

/**
 * Scale `source` to `width` x `height` in halving steps: one big jump drops
 * detail and aliases. The first step stays within Safari's canvas cap.
 */
function resize(
	source: HTMLCanvasElement | HTMLImageElement,
	width: number,
	height: number,
) {
	const longest = Math.max(sizeOf(source).width, sizeOf(source).height)
	const stops: Array<[number, number]> = [[width, height]]
	let [w, h] = [width, height]
	while (
		Math.max(w, h) * 2 < longest &&
		Math.max(w, h) * 2 <= MAX_CANVAS_EDGE
	) {
		;[w, h] = [w * 2, h * 2]
		stops.unshift([w, h])
	}
	let current = source
	for (const [stopWidth, stopHeight] of stops) {
		const next = makeCanvas(stopWidth, stopHeight)
		context(next).drawImage(current, 0, 0, next.width, next.height)
		if (current !== source) release(current as HTMLCanvasElement)
		current = next
	}
	return current as HTMLCanvasElement
}

function toBlob(
	canvas: HTMLCanvasElement,
	type: string,
	quality: number,
): Promise<Blob | null> {
	return new Promise((resolve) => {
		try {
			canvas.toBlob(resolve, type, quality)
		} catch {
			resolve(null)
		}
	})
}

let webpSupport: Promise<boolean> | undefined
/** Safari can't encode WebP and quietly hands back a PNG: test the output */
function canEncodeWebp() {
	webpSupport ??= toBlob(makeCanvas(1, 1), 'image/webp', 0.8).then(
		(blob) => blob?.type === 'image/webp',
	)
	return webpSupport
}

async function compress(canvas: HTMLCanvasElement, quality: number) {
	if (await canEncodeWebp()) {
		const webp = await toBlob(canvas, 'image/webp', quality)
		if (webp?.type === 'image/webp') return webp
	}
	// JPEG has no transparency: see-through logos go on white, not black
	const flat = makeCanvas(canvas.width, canvas.height)
	try {
		const ctx = context(flat)
		ctx.fillStyle = '#ffffff'
		ctx.fillRect(0, 0, flat.width, flat.height)
		ctx.drawImage(canvas, 0, 0)
		const jpeg = await toBlob(flat, 'image/jpeg', quality)
		return jpeg?.type === 'image/jpeg' ? jpeg : null
	} finally {
		release(flat)
	}
}

/**
 * Final resize (never upscale) + compress to WebP, or JPEG where the browser
 * can't make WebP, within UPLOAD_BUDGET_BYTES. Returns the upload file.
 */
export async function encodeSource(
	source: HTMLCanvasElement | HTMLImageElement,
	options: { maxDimension?: number } = {},
): Promise<PreparedImage> {
	const { width, height } = sizeOf(source)
	const fit = Math.min(
		1,
		(options.maxDimension ?? MAX_UPLOAD_DIMENSION) / Math.max(width, height),
	)
	for (const [share, quality] of ENCODE_STEPS) {
		let canvas: HTMLCanvasElement | undefined
		try {
			canvas = resize(source, width * fit * share, height * fit * share)
			const blob = await compress(canvas, quality)
			if (blob && blob.size <= UPLOAD_BUDGET_BYTES) {
				const extension = blob.type === 'image/webp' ? 'webp' : 'jpg'
				return {
					file: new File([blob], `bilete.${extension}`, { type: blob.type }),
					width: canvas.width,
					height: canvas.height,
				}
			}
		} catch {
			// out of canvas memory and the like: a smaller step may still work
		} finally {
			if (canvas) release(canvas)
		}
	}
	throw new Error('Klarte ikkje å førebu biletet. Prøv eit anna bilete.')
}
